import { Save } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  TradeIdeasColumnPatch,
  TradeIdeasColumnMapping,
  TradeIdeasFilter,
} from "@/lib/tradeideas/types";

const NONE = "__none__";
const DATA_TYPES = ["string", "integer", "decimal", "timestamp", "boolean"];

export function ColumnMappingTable({
  columns,
  filters,
  pending,
  onSave,
}: {
  columns: TradeIdeasColumnMapping[];
  filters: TradeIdeasFilter[];
  pending: boolean;
  onSave: (columnId: number, patch: TradeIdeasColumnPatch) => void;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-border">
      <div className="grid grid-cols-[52px_1.2fr_1fr_0.8fr_0.8fr_1.2fr_80px] border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-fg">
        <span>On</span>
        <span>Source column</span>
        <span>Standard key</span>
        <span>Type</span>
        <span>Unit</span>
        <span>Description</span>
        <span className="text-right">Save</span>
      </div>
      {columns.map((column) => (
        <ColumnMappingRow
          key={column.column_id}
          column={column}
          filters={filters}
          pending={pending}
          onSave={onSave}
        />
      ))}
    </section>
  );
}

function ColumnMappingRow({
  column,
  filters,
  pending,
  onSave,
}: {
  column: TradeIdeasColumnMapping;
  filters: TradeIdeasFilter[];
  pending: boolean;
  onSave: (columnId: number, patch: TradeIdeasColumnPatch) => void;
}) {
  const [enabled, setEnabled] = useState(column.enabled);
  const [filterKey, setFilterKey] = useState(column.filter_key || NONE);
  const [dataType, setDataType] = useState(column.data_type || "string");
  const [unit, setUnit] = useState(column.unit || "");
  const [description, setDescription] = useState(column.description || "");
  const selectedFilter = useMemo(
    () => filters.find((filter) => filter.key === filterKey),
    [filterKey, filters],
  );

  function save() {
    onSave(column.column_id, {
      enabled,
      filter_key: filterKey === NONE ? "" : filterKey,
      data_type: dataType,
      unit,
      description,
    });
  }

  return (
    <div className="grid grid-cols-[52px_1.2fr_1fr_0.8fr_0.8fr_1.2fr_80px] items-center gap-2 border-b border-border/60 px-3 py-2 last:border-b-0">
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.currentTarget.checked)}
          className="size-4 accent-accent"
          aria-label={`Enable ${column.source_column}`}
        />
      </label>
      <div className="min-w-0">
        <div className="truncate font-medium">{column.source_column}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className="font-mono text-[11px] text-muted-fg">#{column.position + 1}</span>
          <Badge variant={column.current ? "success" : "outline"}>
            {column.current ? "current" : "stale"}
          </Badge>
        </div>
      </div>
      <Select value={filterKey} onValueChange={setFilterKey}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Unmapped</SelectItem>
          {filters.map((filter) => (
            <SelectItem key={filter.key} value={filter.key}>
              {filter.display_name || filter.tradeideas_name || filter.key}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={dataType} onValueChange={setDataType}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATA_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input value={unit} onChange={(event) => setUnit(event.currentTarget.value)} />
      <Input
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={save}
          disabled={pending}
          title="Save mapping"
        >
          <Save className="size-4" />
        </Button>
      </div>
      {selectedFilter ? (
        <div className="col-span-7 -mt-1 flex min-w-0 flex-wrap gap-1 pl-[calc(52px+0.5rem)] text-[11px] text-muted-fg">
          <Badge variant="outline">{selectedFilter.category || "catalog"}</Badge>
          {selectedFilter.formula ? <span className="truncate">{selectedFilter.formula}</span> : null}
          {!selectedFilter.formula && selectedFilter.description ? (
            <span className="truncate">{selectedFilter.description}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
